import { ROOM_STATE } from 'asteroids-shared/protocol'
import { replayerIcons } from '../replayer/InterfaceControls.js'

const ROOM_STATE_SORT_RANK = {
  [ROOM_STATE.WAITING]: 0,
  [ROOM_STATE.PLAYING]: 1,
  [ROOM_STATE.ENDED]: 2
}

const compareRooms = (a, b) =>
  ROOM_STATE_SORT_RANK[a.state] - ROOM_STATE_SORT_RANK[b.state]
    || Number(a.isPrivate) - Number(b.isPrivate)
    || a.name.localeCompare(b.name)

const SCORE_FILTERS = [
  ['all', 'All'],
  ['coop', 'Co-op'],
  ['deathmatch', 'Deathmatch'],
  ['ctf', 'CTF']
]

const MODE_LABELS = {
  coop: 'Co-op',
  deathmatch: 'Deathmatch',
  ctf: 'CTF'
}

function worldModeLabel(toroidalMap) {
  return toroidalMap === false ? 'Bouncy Walls' : 'Toroidal'
}

function scoreFiltersHtml (selectedFilter) {
  return `
    <div class="score-filters">
      ${SCORE_FILTERS.map(([value, label]) => `
        <button
          type="button"
          data-role="set-score-filter"
          data-value="${value}"
          aria-pressed="${String(selectedFilter === value)}"
        >
          ${label}
        </button>
      `).join('')}
    </div>
  `
}

function scoresHtml (highScores, selectedFilter) {
  const scoreItems = highScores.length
    ? highScores.map((score, index) => `
        <li class="score-row">
          <span>#${index + 1}</span>
          <span>${score.player_name}</span>
          <span class="mode-pill" data-mode="${score.game_mode}">${MODE_LABELS[score.game_mode] ?? score.game_mode}</span>
          <span>${score.final_score} pts</span>
          <span>${score.game_mode === 'coop' ? `${score.waves_cleared} waves` : 'Timed'}</span>
          ${score.replay_id ? `<button type="button" class="replay-link" data-role="watch-replay" data-replay-id="${String(score.replay_id)}" aria-label="Watch replay" title="Watch replay">${replayerIcons.watchReplay}</button>` : '<span></span>'}
        </li>
      `).join('')
    : '<li class="subtle">No scores have been recorded yet.</li>'
  return `${scoreFiltersHtml(selectedFilter)}<ul class="score-list" data-dynamic-list="scores">${scoreItems}</ul>`
}

function newGameHtml (state) {
  return `
    <div class="input-stack">
      <input type="text" data-role="player-name" name="player-name" placeholder="Your name" maxlength="20" value="${state.menu.name}">
      <input type="text" data-role="room-name" name="room-name" placeholder="Game name" maxlength="32" value="${state.menu.roomName}">
    </div>

    <div class="segmented">
      <button type="button" data-role="set-public" aria-pressed="${String(!state.menu.createPrivate)}">Public</button>
      <button type="button" data-role="set-private" aria-pressed="${String(state.menu.createPrivate)}">Private</button>
      <p class="subtle">
      ${state.menu.createPrivate
        ? 'Private games appear in the list, but players need the 4-letter code after they click in.'
        : 'Public games show up in the list and anyone can join while there is room.'}
      </p>
    </div>

    <button type="button" data-role="create-game" ${state.menu.name.trim() ? '' : 'disabled'}>
      ${state.menu.createPrivate ? 'Create private game' : 'Create public game'}
    </button>
  `
}

function roomCardHtml (room, state) {
  const canJoin = room.playerCount < room.maxPlayers && room.state !== ROOM_STATE.PLAYING
  const expanded = state.menu.selectedPrivateRoomId === room.id

  return `
    <li class="room-card" data-room-id="${room.id}">
      <div class="room-copy">
        <div class="room-title">${room.name}</div>
        <div class="room-meta">
          <span class="pill ${room.isPrivate ? 'private' : 'public'}">${room.isPrivate ? 'Private' : 'Public'}</span>
          <span class="pill world">${worldModeLabel(room.toroidalMap)}</span>
          <span>${room.playerCount}/${room.maxPlayers} players</span>
          <span>${room.state === ROOM_STATE.PLAYING
            ? 'In progress'
            : room.playerCount >= room.maxPlayers
              ? 'Full'
              : 'Open'}</span>
        </div>
      </div>
      ${room.isPrivate ? `
        <button
          type="button"
          data-role="toggle-private"
          data-can-join="${String(canJoin)}"
          ${!state.menu.name.trim() || !canJoin ? 'disabled' : ''}
          aria-expanded="${String(expanded)}"
        >
          ${expanded ? 'Cancel' : 'Enter code'}
        </button>
      ` : `
        <button
          type="button"
          data-role="join-public"
          data-can-join="${String(canJoin)}"
          ${!state.menu.name.trim() || !canJoin ? 'disabled' : ''}
        >
          Join
        </button>
      `}
      ${room.isPrivate && expanded ? `
        <div class="room-code-row">
          <input type="text" data-role="join-code" name="join-code" placeholder="CODE" maxlength="4" value="${state.menu.joinCode}">
          <button
            type="button"
            data-role="join-private"
            data-can-join="${String(canJoin)}"
            ${!state.menu.name.trim() || state.menu.joinCode.length !== 4 ? 'disabled' : ''}
          >
            Join private game
          </button>
        </div>
      ` : ''}
    </li>
  `
}

function roomsHtml (state) {
  if (!state.connected) {
    return '<p class="subtle">Connecting to the server...</p>'
  }
  const sortedRooms = state.rooms.toSorted(compareRooms)
  const roomItems = sortedRooms.length
    ? sortedRooms.map(room => roomCardHtml(room, state)).join('')
    : '<li class="subtle">No games are open yet. Create the first one.</li>'
  return `<ul class="room-list" data-dynamic-list="rooms">${roomItems}</ul>`
}

function menuHtml (state) {
  return `
      <section class="menu-intro">
        <p class="eyebrow">MULTIPLAYER ASTEROIDS</p>
        <h1>ASTEROIDS</h1>
        <p>Pick a name, create a room, or jump into one that is already waiting.</p>
      </section>

      <section class="menu-section" data-panel="create">
        <h2>Create a game</h2>
        ${newGameHtml(state)}
      </section>

      <section class="menu-section" data-panel="rooms">
        <h2>Available games</h2>
        ${roomsHtml(state)}
      </section>

      <section class="menu-section" data-panel="scores">
        <h2>High scores</h2>
        ${scoresHtml(state.highScores, state.menu.scoreModeFilter)}
      </section>
  `
}

const syncDisabledStates = root => {
  const hasName = root.querySelector('[data-role="player-name"]').value.trim()
  root.querySelector('[data-role="create-game"]').disabled = !hasName

  root.querySelectorAll(
    '[data-role="join-public"], [data-role="toggle-private"], [data-role="join-private"]'
  ).forEach(button => {
    button.disabled = !hasName
      || button.dataset.canJoin !== 'true'
      || (button.getAttribute('data-role') === 'join-private' 
        && root.querySelector('[data-role="join-code"]').value.length !== 4)
  })
}

const JOIN_CODE_ERROR_ROLE = 'join-code-error'

const patchErrorRegion = (root, state) => {
  root.querySelectorAll(`[data-role="${JOIN_CODE_ERROR_ROLE}"]`).forEach(el => el.remove())

  const msg = state.error
  const placement = state.errorPlacement

  const inlineOk =
    placement === 'join-code'
    && msg
    && state.menu.selectedPrivateRoomId

  if (!msg) {
    root.querySelector(':scope > .error-text')?.remove()
    return
  }

  if (inlineOk) {
    root.querySelector(':scope > .error-text')?.remove()
    const roomsSection = root.querySelector('[data-panel="rooms"]')
    const rid = state.menu.selectedPrivateRoomId
    const card = roomsSection?.querySelector(`[data-room-id="${CSS.escape(rid)}"]`)
    const codeRow = card?.querySelector('.room-code-row')
    if (codeRow && card) {
      const p = document.createElement('p')
      p.className = 'error-text'
      p.setAttribute('data-role', JOIN_CODE_ERROR_ROLE)
      p.textContent = msg
      codeRow.after(p)
      return
    }
  }

  let globalEl = root.querySelector(':scope > .error-text')
  if (!globalEl) {
    globalEl = document.createElement('p')
    globalEl.className = 'error-text'
    root.appendChild(globalEl)
  }
  globalEl.textContent = msg
}

function syncScoreFilterPressed (root, selectedFilter) {
  root.querySelectorAll('[data-role="set-score-filter"]').forEach(btn => {
    btn.setAttribute('aria-pressed', String(selectedFilter === btn.dataset.value))
  })
}

function patchScoreList (root, highScores) {
  const ul = root.querySelector('ul[data-dynamic-list="scores"]')
  if (!ul) return
  ul.innerHTML = highScores.length
    ? highScores.map((score, index) => `
        <li class="score-row">
          <span>#${index + 1}</span>
          <span>${score.player_name}</span>
          <span class="mode-pill" data-mode="${score.game_mode}">${MODE_LABELS[score.game_mode] ?? score.game_mode}</span>
          <span>${score.final_score} pts</span>
          <span>${score.game_mode === 'coop' ? `${score.waves_cleared} waves` : 'Timed'}</span>
          ${score.replay_id ? `<button type="button" class="replay-link" data-role="watch-replay" data-replay-id="${String(score.replay_id)}" aria-label="Watch replay" title="Watch replay">${replayerIcons.watchReplay}</button>` : '<span></span>'}
        </li>
      `).join('')
    : '<li class="subtle">No scores have been recorded yet.</li>'
}

function roomsListVisualKey (state) {
  if (!state.connected) return 'disconnected'
  const sortedRooms = state.rooms.toSorted(compareRooms)
  const rooms = sortedRooms.map(r => ({
    id: r.id,
    name: r.name,
    state: r.state,
    playerCount: r.playerCount,
    maxPlayers: r.maxPlayers,
    toroidalMap: r.toroidalMap,
    isPrivate: r.isPrivate
  }))
  return JSON.stringify({
    rooms,
    selectedPrivateRoomId: state.menu.selectedPrivateRoomId
  })
}

function patchRoomsBlock (root, state) {
  const section = root.querySelector('[data-panel="rooms"]')
  if (!section) return
  const h2 = section.querySelector(':scope > h2')
  if (!h2) return

  const key = roomsListVisualKey(state)
  if (root._roomsListVisualKey === key) return
  root._roomsListVisualKey = key

  const html = roomsHtml(state).trim()
  const t = document.createElement('template')
  t.innerHTML = html
  const next = h2.nextElementSibling
  const newEl = t.content.firstElementChild
  if (!newEl) return

  if (!next) {
    h2.after(newEl)
    return
  }
  if (next.tagName === 'UL' && newEl.tagName === 'UL') {
    next.innerHTML = newEl.innerHTML
    return
  }
  next.replaceWith(newEl)
}

/** Keeps the section’s static `<h2>`; replaces everything after it with parsed `htmlString`. */
function patchSectionAfterHeading(section, htmlString) {
  const h2 = section.querySelector(':scope > h2')
  if (!h2) return
  let n = h2.nextSibling
  while (n) {
    const next = n.nextSibling
    n.remove()
    n = next
  }
  const t = document.createElement('template')
  t.innerHTML = htmlString.trim()
  section.append(...t.content.childNodes)
}

const wireMenuPanel = (root, actions) => {
  root._menuWireAbort?.abort()
  const ac = new AbortController()
  root._menuWireAbort = ac
  const { signal } = ac

  const onClick = {
    'set-public': () => actions.setCreatePrivate(false),
    'set-private': () => actions.setCreatePrivate(true),
    'create-game': () => actions.createGame(),
    'set-score-filter': t => actions.setScoreModeFilter(t.dataset.value),
    'join-public': t => actions.joinRoom(t.closest('[data-room-id]').dataset.roomId, ''),
    'toggle-private': t => actions.togglePrivateRoom(t.closest('[data-room-id]').dataset.roomId),
    'join-private': () => {
      const s = actions.getState()
      actions.joinRoom(
        root.querySelector(`[data-room-id="${CSS.escape(s.menu.selectedPrivateRoomId)}"]`).dataset.roomId,
        s.menu.joinCode
      )
    },
    'watch-replay': t => actions.openReplay(t.dataset.replayId)
  }

  const onInput = {
    'player-name': t => {
      actions.setName(t.value)
      syncDisabledStates(root)
    },
    'room-name': t => actions.setRoomName(t.value),
    'join-code': t => {
      actions.setJoinCode(t.value)
      t.value = actions.getState().menu.joinCode
      actions.clearMenuError()
      syncDisabledStates(root)
    }
  }

  root.addEventListener('click', e => {
    const target = e.target.closest('[data-role]')
    const handler = target ? onClick[target.getAttribute('data-role')] : null
    if (typeof handler === 'function') handler(target)
  }, { signal })

  root.addEventListener('input', e => {
    const target = e.target
    const handler = target ? onInput[target.getAttribute('data-role')] : null
    if (typeof handler === 'function') handler(target)
  }, { signal })
}

export function renderMenu (root, state, actions) {
  const menuMounted = root.querySelector('.menu-intro')

  if (!menuMounted) {
    delete root._roomsListVisualKey
    delete root._menuPrevSelectedPrivate
    root.innerHTML = menuHtml(state)
    patchErrorRegion(root, state)
    root._menuLastCreatePrivate = state.menu.createPrivate
    wireMenuPanel(root, actions)
  } else {
    const scrollTop = root.scrollTop
    const prevSelected = root._menuPrevSelectedPrivate ?? null
    patchRoomsBlock(root, state)
    patchScoreList(root, state.highScores)
    syncScoreFilterPressed(root, state.menu.scoreModeFilter)
    patchErrorRegion(root, state)

    if (root._menuLastCreatePrivate !== state.menu.createPrivate) {
      patchSectionAfterHeading(root.querySelector('[data-panel="create"]'), newGameHtml(state))
      root._menuLastCreatePrivate = state.menu.createPrivate
    }

    root.scrollTop = scrollTop

    const sel = state.menu.selectedPrivateRoomId
    if (sel != null && sel !== prevSelected) {
      queueMicrotask(() => {
        const input = root.querySelector('[data-role="join-code"]')
        if (input instanceof HTMLInputElement) input.focus()
      })
    }
    root._menuPrevSelectedPrivate = sel
  }

  syncDisabledStates(root)
}
